import { helpers, mui, React, ts, ui } from '_core';

import { Tab, transform } from './helpers';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  riskModel: ts.types.riskModel.RiskModel;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, file, fullScreen, riskModel, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState(null);
  const [selectedTab, setSelectedTab] = React.useState<Tab>('total');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          rename: {
            build_date: 'date',
            realized_risk: 'Realized Risk',
            forecast_risk: 'Forecast Risk',
            realized_minus_forecast_risk: 'Realized - Forecast Risk',
            forecast_specific_risk: 'Forecast Specific Risk',
            realized_specific_risk: 'Realized Specific Risk',
            realized_minus_forecast_specific_risk: 'Realized - Forecast Specific Risk',
          },
        },
      });
      if (response) setData(response.data);
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const formattedData = React.useMemo(() => {
    return transform(data, selectedTab, riskModel);
  }, [data, selectedTab, riskModel]);

  const getTabs = () => {
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(_e, newTab) => setSelectedTab(newTab)}
      >
        <mui.core.Tab label="Total" value="total" />
        <mui.core.Tab label="Style Factors" value="style" />
        <mui.core.Tab label="Industries" value="industries" />
        <mui.core.Tab label="Specific" value="specific" />
        <mui.core.Tab label="Specific by Quantile" value="specific_by_quantile" />
      </mui.core.Tabs>
    );
  };

  let yAxisLabel;
  if (selectedTab == 'total' || selectedTab == 'specific') {
    yAxisLabel = 'Annualized Portfolio Risk';
  } else if (selectedTab == 'specific_by_quantile') {
    yAxisLabel = 'Avg Realized - Forecast Annualized Asset Risk';
  } else {
    yAxisLabel = 'Realized - Forecast Annualized Portfolio Risk';
  }

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.LineChart
          id={id}
          data={formattedData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
          yAxisLabel={yAxisLabel}
          numberFormat="#.##%"
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
